import React, { useEffect, useMemo, useState } from "react";
import { Stack, Typography, StackProps, Link, Switch, FormControlLabel, Alert } from "@mui/material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { GetOrderProductReadableType } from "../../../Utils/helpers";
import OrderProductIcon from "../../Misc/OrderProductIcon/order-product-icon";
import { useAppSelector } from "../../../Redux/redux-store";
import {
  BasketShowOnlyAvailable,
  GetItemPriceRule,
  ShowOnlyAvailableToggleBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import { useDispatch, useSelector } from "react-redux";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import { useTranslation } from "react-i18next";

interface ConfigHeaderProps extends StackProps {
  title?: string;
  configType: OrderProductType;
  itemId: number;
  handleCancel: () => void;
  handleChangeProduct: () => void;
}

const ConfigHeader: React.FC<ConfigHeaderProps> = (props) => {
  const { t } = useTranslation();
  const { configType, title, itemId, handleCancel, handleChangeProduct, ...otherProps } = props;

  const [resetToggleAfterLeave, setResetToggleAfterLeave] = useState(false);

  const dispatch = useDispatch();
  const toggleAvailableOnly = useAppSelector(BasketShowOnlyAvailable);
  const priceRule = useSelector(GetItemPriceRule(itemId));

  const toggleShow =
    configType === OrderProductType.TranscodeGeneric ||
    configType === OrderProductType.TranscodeGenericSingleFile ||
    configType === OrderProductType.TranscodeDownscaleAsMezz ||
    configType === OrderProductType.TranscodeImf ||
    configType === OrderProductType.TranscodeDcp ||
    configType === OrderProductType.TranscodeSequence;

  const handleSwitchAvailable = () => {
    dispatch(ShowOnlyAvailableToggleBasket());
  };

  const cancel = () => {
    handleCancel();
  };

  const changeProduct = () => {
    handleChangeProduct();
  };

  const priceRulePrice = useMemo(() => {
    if (!priceRule) return 0;

    let price = 0;

    priceRule.rules.forEach((rule) => {
      price += rule?.computedPrice?.find((x) => x.currency === CurrencyTypes.EUR)?.price || 0;
    });

    return price;
  }, [priceRule]);

  useEffect(() => {
    if (configType === OrderProductType.TranscodeByProfile && toggleAvailableOnly) {
      setResetToggleAfterLeave(true);
      handleSwitchAvailable();
    }
    return () => {
      if (resetToggleAfterLeave) handleSwitchAvailable();
    };
  }, [toggleAvailableOnly, resetToggleAfterLeave]);

  return (
    <>
      <Stack flex={1} direction="row" alignItems="center" justifyContent="space-between" {...otherProps} mb={2}>
        <Stack direction="row" spacing={1}>
          <OrderProductIcon type={configType} />
          <Typography variant="subtitle2" component="h3" fontWeight={600}>
            {title ? title : GetOrderProductReadableType(configType)}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          {toggleShow && (
            <Typography color="text.tier">
              <FormControlLabel
                sx={{ m: 0 }}
                control={<Switch size="small" checked={toggleAvailableOnly} onChange={() => handleSwitchAvailable()} />}
                label={t("field:label.showOnlyAvailableValues")}
              />
            </Typography>
          )}

          <Link component="button" color="primary.text" underline="hover" onClick={() => changeProduct()}>
            {t("button:changeProduct")}
          </Link>
          <Link component="button" color="primary.text" underline="hover" onClick={() => cancel()}>
            {t("common:cancel")}
          </Link>
        </Stack>
      </Stack>
      {priceRule !== undefined && priceRule.rules.some((item) => item !== null) && (
        <Stack direction="row" mb={2} width={"100%"}>
          <Alert color="info" icon={<MonetizationOnIcon />} sx={{ width: "100%" }}>
            {t("alert:info.configIsBilled", { name: priceRule.rules.map((rule) => rule?.name), price: priceRulePrice })}
          </Alert>
        </Stack>
      )}
    </>
  );
};

export default ConfigHeader;
